import { Button } from '@/components/ui';
import { PhoneInput } from './PhoneInput';
import { createDefaultPhoneFormItem } from '../types';
import type { PhoneFormItem } from '../types';

interface PhoneInputListProps {
  phones: PhoneFormItem[];
  onChange: (phones: PhoneFormItem[]) => void;
  maxPhones?: number;
  showAdvanced?: boolean;
  disabled?: boolean;
  errors?: Record<string, { phoneNumber?: string }>;
}

export function PhoneInputList({
  phones,
  onChange,
  maxPhones = 5,
  showAdvanced = false,
  disabled = false,
  errors = {},
}: PhoneInputListProps) {
  const handlePhoneChange = (index: number, updatedPhone: PhoneFormItem) => {
    const newPhones = [...phones];

    // If setting this phone as primary, clear primary from others
    if (updatedPhone.isPrimary && !phones[index].isPrimary) {
      newPhones.forEach((p, i) => {
        if (i !== index) {
          newPhones[i] = { ...p, isPrimary: false };
        }
      });
    }

    newPhones[index] = updatedPhone;
    onChange(newPhones);
  };

  const handleAddPhone = () => {
    if (phones.length >= maxPhones) return;

    const newPhone = createDefaultPhoneFormItem(phones.length === 0);
    onChange([...phones, newPhone]);
  };

  const handleRemovePhone = (index: number) => {
    const newPhones = phones.filter((_, i) => i !== index);

    // If we removed the primary phone, make the first one primary
    if (phones[index].isPrimary && newPhones.length > 0) {
      newPhones[0] = { ...newPhones[0], isPrimary: true };
    }

    onChange(newPhones);
  };

  const canAddMore = phones.length < maxPhones;
  const canRemove = phones.length > 1;

  return (
    <div className="space-y-3">
      {/* Phone List */}
      {phones.map((phone, index) => (
        <PhoneInput
          key={phone.tempId}
          phone={phone}
          onChange={(p) => handlePhoneChange(index, p)}
          onRemove={canRemove ? () => handleRemovePhone(index) : undefined}
          showRemove={canRemove}
          showPrimaryToggle={phones.length > 1}
          showAdvanced={showAdvanced}
          disabled={disabled}
          errors={errors[phone.tempId]}
          index={phones.length > 1 ? index : undefined}
        />
      ))}

      {/* Add Phone Button */}
      {canAddMore && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddPhone}
          disabled={disabled}
          className="w-full"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Another Phone
        </Button>
      )}

      {/* Max phones hint */}
      {!canAddMore && (
        <p className="text-xs text-secondary-500 text-center">
          Maximum of {maxPhones} phone numbers allowed
        </p>
      )}
    </div>
  );
}
